import { TcsSchema, TgtLogger } from '@measurement/opentelemetry-web'
import { getActiveTraceContext } from 'utils/traceContext'

/** Fallback string used when an error has no stack trace. */
export const NO_STACK_TRACE = 'No stack trace available'

const sensitiveUrlParameters = new Set([
    'access_token',
    'refresh_token',
    'id_token',
    'client_secret',
    'token',
    'code',
])
const sensitiveUrlParameterPattern = new RegExp(
    `([?#&])(${[...sensitiveUrlParameters].join('|')})=[^&#]*`,
    'gi',
)

type SafeTgtLoggerSchema = TcsSchema & {
    trace_id?: string
    span_id?: string
    nonIndexedFields?: Record<string, TcsSchema>
}
/**
 * Safe wrapper for TgtLogger that catches any synchronous errors.
 * TgtLogger methods return void but may trigger internal async operations.
 * This wrapper ensures any synchronous errors don't crash the app.
 *
 * Every log emission is automatically enriched with the active OTel trace_id
 * and span_id (when a span is in context). This means even logs emitted in
 * catch blocks or error boundaries will carry trace context when possible,
 * making it easier to correlate log entries with traces in Grafana Tempo.
 */
export const safeTgtLogger = {
    info: (message: string, attributes?: SafeTgtLoggerSchema) => {
        try {
            TgtLogger.info(message, getCombinedAttributes(attributes))
        } catch (e) {
            // Fall back to console so OTEL failures don't go completely unnoticed
            // eslint-disable-next-line no-console
            console.error('[safeTgtLogger] TgtLogger.info failed', e)
        }
    },

    warn: (message: string, attributes?: SafeTgtLoggerSchema) => {
        try {
            TgtLogger.warn(message, getCombinedAttributes(attributes))
        } catch (e) {
            // Fall back to console so OTEL failures don't go completely unnoticed
            // eslint-disable-next-line no-console
            console.error('[safeTgtLogger] TgtLogger.warn failed', e)
        }
    },

    error: (message: string, attributes?: SafeTgtLoggerSchema) => {
        try {
            TgtLogger.error(message, getCombinedAttributes(attributes))
        } catch (e) {
            // Fall back to console so OTEL failures don't go completely unnoticed
            // eslint-disable-next-line no-console
            console.error('[safeTgtLogger] TgtLogger.error failed', e)
        }
    },
}

// Common attributes we want to add to every log emission
// Note: If you add a new common attribute here,
// make sure to update getCombinedAttributes to properly merge it with any attributes passed to the log method.
const getCommonAttributes = (): SafeTgtLoggerSchema => {
    const traceCtx = getActiveTraceContext()

    return {
        ...traceCtx,
        nonIndexedFields: {
            page: sanitizeUrl(window.location.href),
        },
    }
}

const sanitizeUrl = (href: string): string => {
    return href.replace(sensitiveUrlParameterPattern, '$1$2=[REDACTED]')
}

// Combines attributes sent to the specific log method with the common attributes we want to add to every log emission
const getCombinedAttributes = (
    attributes?: SafeTgtLoggerSchema,
): SafeTgtLoggerSchema => {
    const { trace_id, span_id, nonIndexedFields } = getCommonAttributes()
    const {
        nonIndexedFields: nonIndexedFieldsAttr,
        url: urlAttributes,
        ...restAttributes
    } = attributes ?? {}
    return {
        ...restAttributes,
        trace_id,
        ...(span_id ? { span_id } : {}),
        nonIndexedFields: {
            ...nonIndexedFields,
            ...nonIndexedFieldsAttr,
            ...(typeof nonIndexedFieldsAttr?.page === 'string'
                ? { page: sanitizeUrl(nonIndexedFieldsAttr.page) }
                : {}),
        },
        ...(urlAttributes
            ? {
                  url: {
                      ...urlAttributes,
                      ...(urlAttributes.full
                          ? { full: sanitizeUrl(urlAttributes.full) }
                          : {}),
                      ...(urlAttributes.query
                          ? {
                                query: sanitizeUrl(
                                    `?${urlAttributes.query}`,
                                ).slice(1),
                            }
                          : {}),
                  },
              }
            : {}),
        ...restAttributes,
    }
}
